import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { RedirectCn03RgFromManage } from '~/components/RedirectCn03RgFromManage';
import { RequirePermission } from '~/components/RequirePermission';
import {
  CN01_MANAGE_ACCESS_PERMISSIONS,
  CN02_FORM_ACCESS_PERMISSIONS,
  CN03_FORM_ACCESS_PERMISSIONS,
  CN03_FORM_VIEW_ACCESS_PERMISSIONS,
  CN03_RG_MANAGE_ACCESS_PERMISSIONS,
  CN04_FORM_CREATE_PERMISSIONS,
  CN04_FORM_ROUTE_PERMISSIONS,
  CN04_MANAGE_ACCESS_PERMISSIONS,
  CN05_FORM_CREATE_PERMISSIONS,
  CN05_FORM_ROUTE_PERMISSIONS,
  CN05_MANAGE_ACCESS_PERMISSIONS,
  CN06_FORM_CREATE_PERMISSIONS,
  CN06_FORM_ROUTE_PERMISSIONS,
  CN06_MANAGE_ACCESS_PERMISSIONS,
  FINANCIAL_MANAGE_ACCESS_PERMISSIONS,
  FINANCIAL_PLANTER_CALCULATE_PERMISSIONS,
  REPORTS_MODULE_ACCESS_PERMISSIONS,
  ROLES_MODULE_ACCESS_PERMISSIONS,
  PEST_DISEASE_MANAGE_ACCESS_PERMISSIONS,
  PEST_DISEASE_PLANTER_ACCESS_PERMISSIONS,
  SUPPLIER_MODULE_ACCESS_PERMISSIONS,
  USERS_MODULE_ACCESS_PERMISSIONS,
} from '~/lib/permissions';
import { DashboardPage } from '~/pages/dashboard/DashboardPage';
import { ProtectedLayout } from '~/layouts/ProtectedLayout';
import { SettingsPage } from '~/pages/settings/SettingsPage';

const LazyCN02ManagePage = lazy(async () => {
  const mod = await import('~/pages/cn02/manage');
  return { default: mod.CN02ManagePage };
});

const LazyCN02CertificatePage = lazy(async () => {
  const mod = await import('~/pages/cn02/certificate');
  return { default: mod.CN02CertificatePage };
});

const LazyCN01ManagePage = lazy(async () => {
  const mod = await import('~/pages/cn01/manage');
  return { default: mod.CN01ManagePage };
});

const LazyStaffCn01ViewPage = lazy(async () => {
  const mod = await import('~/pages/cn01/print/StaffCn01ViewPage');
  return { default: mod.StaffCn01ViewPage };
});

const LazyStaffCn01DetailPage = lazy(async () => {
  const mod = await import('~/pages/cn01/detail');
  return { default: mod.StaffCn01DetailPage };
});

const LazyCN03FormPage = lazy(async () => {
  const mod = await import('~/pages/cn03/form');
  return { default: mod.CN03FormPage };
});

const LazyCN03ManagePage = lazy(async () => {
  const mod = await import('~/pages/cn03/manage');
  return { default: mod.CN03ManagePage };
});

const LazyCN03ManageRgPage = lazy(async () => {
  const mod = await import('~/pages/cn03/manageRg');
  return { default: mod.CN03ManageRgPage };
});

const LazyCN03OfflineListPage = lazy(async () => {
  const mod = await import('~/pages/cn03/offline/list');
  return { default: mod.CN03OfflineListPage };
});

const LazyCN03OfflineFormPage = lazy(async () => {
  const mod = await import('~/pages/cn03/offline');
  return { default: mod.CN03OfflineFormPage };
});

const LazyCN04ManagePage = lazy(async () => {
  const mod = await import('~/pages/cn04/manage');
  return { default: mod.CN04ManagePage };
});

const LazyCN04FormPage = lazy(async () => {
  const mod = await import('~/pages/cn04/form');
  return { default: mod.CN04FormPage };
});

const LazyCN04OfflineListPage = lazy(async () => {
  const mod = await import('~/pages/cn04/offline/list');
  return { default: mod.CN04OfflineListPage };
});

const LazyCN04OfflineFormPage = lazy(async () => {
  const mod = await import('~/pages/cn04/offline');
  return { default: mod.CN04OfflineFormPage };
});

const LazyCN05ManagePage = lazy(async () => {
  const mod = await import('~/pages/cn05/manage');
  return { default: mod.CN05ManagePage };
});

const LazyCN05FormPage = lazy(async () => {
  const mod = await import('~/pages/cn05/form');
  return { default: mod.CN05FormPage };
});

const LazyCN05OfflineListPage = lazy(async () => {
  const mod = await import('~/pages/cn05/offline/list');
  return { default: mod.CN05OfflineListPage };
});

const LazyCN05OfflineFormPage = lazy(async () => {
  const mod = await import('~/pages/cn05/offline');
  return { default: mod.CN05OfflineFormPage };
});

const LazyCN06ManagePage = lazy(async () => {
  const mod = await import('~/pages/cn06/manage');
  return { default: mod.CN06ManagePage };
});

const LazyCN06FormPage = lazy(async () => {
  const mod = await import('~/pages/cn06/form');
  return { default: mod.CN06FormPage };
});

const LazyCN06OfflineListPage = lazy(async () => {
  const mod = await import('~/pages/cn06/offline/list');
  return { default: mod.CN06OfflineListPage };
});

const LazyCN06OfflineFormPage = lazy(async () => {
  const mod = await import('~/pages/cn06/offline');
  return { default: mod.CN06OfflineFormPage };
});

const LazyUsersManagePage = lazy(async () => {
  const mod = await import('~/pages/users/manage');
  return { default: mod.UsersManagePage };
});

const LazyUserFormPage = lazy(async () => {
  const mod = await import('~/pages/users/form');
  return { default: mod.UserFormPage };
});

const LazyRolesManagePage = lazy(async () => {
  const mod = await import('~/pages/roles/manage');
  return { default: mod.RolesManagePage };
});

const LazyRoleFormPage = lazy(async () => {
  const mod = await import('~/pages/roles/form');
  return { default: mod.RoleFormPage };
});

const LazyReportsPage = lazy(async () => {
  const mod = await import('~/pages/reports');
  return { default: mod.ReportsPage };
});

const LazySuppliersManagePage = lazy(async () => {
  const mod = await import('~/pages/supplier/manage');
  return { default: mod.SuppliersManagePage };
});

const LazySupplierStaffFormPage = lazy(async () => {
  const mod = await import('~/pages/supplier/manage/form');
  return { default: mod.SupplierStaffFormPage };
});

const LazyPestDiseaseManagePage = lazy(async () => {
  const mod = await import('~/pages/pest-disease/manage');
  return { default: mod.PestDiseaseManagePage };
});

const LazyPestDiseaseBrowsePage = lazy(async () => {
  const mod = await import('~/pages/pest-disease/browse');
  return { default: mod.PestDiseaseBrowsePage };
});

const LazyFinancialManagePage = lazy(async () => {
  const mod = await import('~/pages/financial/manage');
  return { default: mod.FinancialManagePage };
});

const LazyFinancialFormPage = lazy(async () => {
  const mod = await import('~/pages/financial/form');
  return { default: mod.FinancialFormPage };
});

const LazyFinancialViewPage = lazy(async () => {
  const mod = await import('~/pages/financial/view');
  return { default: mod.FinancialViewPage };
});

const LazyFinancialCalculatePage = lazy(async () => {
  const mod = await import('~/pages/financial/calculate');
  return { default: mod.FinancialCalculatePage };
});

const LazyFinancialEstimatePage = lazy(async () => {
  const mod = await import('~/pages/financial/estimate');
  return { default: mod.FinancialEstimatePage };
});

export function PrivateRoutes() {
  return (
    <ProtectedLayout>
      <Routes>
        {/* Dashboard and settings: any authenticated user with a successful /users/me load. */}
        <Route path="dashboard" element={<DashboardPage />} />
        <Route
          path="cn02-manage"
          element={
            <RequirePermission requiredAny={CN02_FORM_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN02ManagePage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn02-certificate"
          element={
            <RequirePermission requiredAny={CN02_FORM_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN02CertificatePage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn01-manage"
          element={
            <RequirePermission requiredAny={CN01_MANAGE_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN01ManagePage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn01-detail/:cn01FormId"
          element={
            <RequirePermission requiredAny={CN01_MANAGE_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyStaffCn01DetailPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn01-view/:cn01FormId"
          element={
            <RequirePermission requiredAny={CN01_MANAGE_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyStaffCn01ViewPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn03-manage"
          element={
            <RequirePermission requiredAny={CN03_FORM_ACCESS_PERMISSIONS}>
              <RedirectCn03RgFromManage>
                <Suspense fallback={<LoadingSpinner />}>
                  <LazyCN03ManagePage />
                </Suspense>
              </RedirectCn03RgFromManage>
            </RequirePermission>
          }
        />
        <Route
          path="cn03-manage/rg"
          element={
            <RequirePermission requiredAny={CN03_RG_MANAGE_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN03ManageRgPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn03-form/:formId?"
          element={
            <RequirePermission requiredAny={CN03_FORM_VIEW_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN03FormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn03-offline"
          element={
            <RequirePermission requiredAny={CN03_FORM_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN03OfflineListPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn03-offline/:id"
          element={
            <RequirePermission requiredAny={CN03_FORM_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN03OfflineFormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn04-manage"
          element={
            <RequirePermission requiredAny={CN04_MANAGE_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN04ManagePage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn04-form"
          element={
            <RequirePermission requiredAny={CN04_FORM_CREATE_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN04FormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn04-form/:formId"
          element={
            <RequirePermission requiredAny={CN04_FORM_ROUTE_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN04FormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn04-offline"
          element={
            <RequirePermission requiredAny={CN04_FORM_CREATE_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN04OfflineListPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn04-offline/:id"
          element={
            <RequirePermission requiredAny={CN04_FORM_CREATE_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN04OfflineFormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn05-manage"
          element={
            <RequirePermission requiredAny={CN05_MANAGE_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN05ManagePage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn05-form"
          element={
            <RequirePermission requiredAny={CN05_FORM_CREATE_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN05FormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn05-form/:formId"
          element={
            <RequirePermission requiredAny={CN05_FORM_ROUTE_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN05FormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn05-offline"
          element={
            <RequirePermission requiredAny={CN05_FORM_CREATE_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN05OfflineListPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn05-offline/:id"
          element={
            <RequirePermission requiredAny={CN05_FORM_CREATE_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN05OfflineFormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn06-manage"
          element={
            <RequirePermission requiredAny={CN06_MANAGE_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN06ManagePage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn06-form"
          element={
            <RequirePermission requiredAny={CN06_FORM_CREATE_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN06FormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn06-form/:formId"
          element={
            <RequirePermission requiredAny={CN06_FORM_ROUTE_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN06FormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn06-offline"
          element={
            <RequirePermission requiredAny={CN06_FORM_CREATE_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN06OfflineListPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="cn06-offline/:id"
          element={
            <RequirePermission requiredAny={CN06_FORM_CREATE_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyCN06OfflineFormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        {/* Staff CN01: use RequirePermission with CN01_FORM_ACCESS_PERMISSIONS when enabled. */}
        {/* <Route path="cn01-form" element={<CN01FormPage />} /> */}
        <Route
          path="users-manage"
          element={
            <RequirePermission requiredAny={USERS_MODULE_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyUsersManagePage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="users/create"
          element={
            <RequirePermission requiredAny={['users_create']}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyUserFormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="users/edit/:userId"
          element={
            <RequirePermission requiredAny={['users_edit']}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyUserFormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="roles-manage"
          element={
            <RequirePermission requiredAny={ROLES_MODULE_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRolesManagePage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="roles/create"
          element={
            <RequirePermission requiredAny={['roles_create']}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoleFormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="roles/edit/:roleId"
          element={
            <RequirePermission requiredAny={['roles_edit']}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyRoleFormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="reports"
          element={
            <RequirePermission requiredAny={REPORTS_MODULE_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyReportsPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="suppliers-manage"
          element={
            <RequirePermission requiredAny={SUPPLIER_MODULE_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazySuppliersManagePage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="suppliers/:supplierId"
          element={
            <RequirePermission requiredAny={SUPPLIER_MODULE_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazySupplierStaffFormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="pest-disease-manage"
          element={
            <RequirePermission requiredAny={PEST_DISEASE_MANAGE_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyPestDiseaseManagePage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="pest-disease"
          element={
            <RequirePermission requiredAny={PEST_DISEASE_PLANTER_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyPestDiseaseBrowsePage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="financial-manage"
          element={
            <RequirePermission requiredAny={FINANCIAL_MANAGE_ACCESS_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyFinancialManagePage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="financial/create"
          element={
            <RequirePermission requiredAny={['financial_create']}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyFinancialFormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="financial/edit/:financialId"
          element={
            <RequirePermission requiredAny={['financial_edit']}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyFinancialFormPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="financial/view/:financialId"
          element={
            <RequirePermission requiredAny={['financial_view']}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyFinancialViewPage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="financial/calculate/:financialId"
          element={
            <RequirePermission requiredAny={['financial_calculate']}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyFinancialCalculatePage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route
          path="financial-estimate"
          element={
            <RequirePermission requiredAny={FINANCIAL_PLANTER_CALCULATE_PERMISSIONS}>
              <Suspense fallback={<LoadingSpinner />}>
                <LazyFinancialEstimatePage />
              </Suspense>
            </RequirePermission>
          }
        />
        <Route path="settings" element={<SettingsPage />} />
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
      </Routes>
    </ProtectedLayout>
  );
}
